import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { useJsApiLoader, LoadScript } from '@react-google-maps/api';

// Create context
const GoogleMapsContext = createContext(null);

// Libraries to load
const LIBRARIES = ['places', 'geometry'];

// Default options for the Google Maps API
const defaultOptions = {
  googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  libraries: LIBRARIES,
  id: 'google-maps-script',
  preventGoogleFontsLoading: true,
  version: 'beta', // Use beta version for latest features
  mapIds: []
};



export const GoogleMapsProvider = ({ children, options = {} }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [map, setMap] = useState(null);
  const [autocompleteService, setAutocompleteService] = useState(null);
  const [geocoder, setGeocoder] = useState(null);
  const [placesService, setPlacesService] = useState(null);

  const mergedOptions = useMemo(() => ({
    ...defaultOptions,
    ...options,
  }), [options]);

  // Initialize the Google Maps API loader
  const { isLoaded: isApiLoaded, loadError: apiLoadError } = useJsApiLoader({
    googleMapsApiKey: mergedOptions.googleMapsApiKey,
    libraries: mergedOptions.libraries,
    id: mergedOptions.id,
    preventGoogleFontsLoading: mergedOptions.preventGoogleFontsLoading,
    version: mergedOptions.version,
    mapIds: mergedOptions.mapIds,
    nonce: mergedOptions.nonce,
    language: mergedOptions.language,
    region: mergedOptions.region,
    authReferrerPolicy: mergedOptions.authReferrerPolicy,
  });

  // Initialize services when the API is loaded
  useEffect(() => {
    if (isApiLoaded && window.google && !isLoaded) {
      try {
        // Initialize services
        setGeocoder(new window.google.maps.Geocoder());
        
        // Initialize Places Service with a temporary element
        const mapElement = document.createElement('div');
        setPlacesService(new window.google.maps.places.PlacesService(mapElement));
        
        // Initialize Autocomplete Service
        setAutocompleteService(new window.google.maps.places.AutocompleteService());
        
        setIsLoaded(true);
      } catch (error) {
        console.error('Error initializing Google Maps services:', error);
        setLoadError(error);
      }
    }
  }, [isApiLoaded, isLoaded]);

  // Handle loading errors
  useEffect(() => {
    if (apiLoadError) {
      console.error('Error loading Google Maps API:', apiLoadError);
      setLoadError(apiLoadError);
    }
  }, [apiLoadError]);

  // Calculate distance between two points using the Distance Matrix Service
  const calculateDistance = async (origin, destination, mode = 'DRIVING') => {
    if (!window.google?.maps?.DistanceMatrixService) {
      console.error('Google Maps Distance Matrix Service not available');
      return null;
    }

    return new Promise((resolve) => {
      try {
        const service = new window.google.maps.DistanceMatrixService();
        service.getDistanceMatrix({
          origins: [origin],
          destinations: [destination],
          travelMode: window.google.maps.TravelMode[mode],
          unitSystem: window.google.maps.UnitSystem.METRIC,
          avoidHighways: false,
          avoidTolls: false,
        }, (response, status) => {
          if (status === 'OK' && 
              response?.rows?.[0]?.elements?.[0]?.status === 'OK' &&
              response.rows[0].elements[0].distance) {
            // Convert meters to kilometers and round to 2 decimal places
            const distanceInKm = parseFloat((response.rows[0].elements[0].distance.value / 1000).toFixed(2));
            resolve(distanceInKm);
          } else {
            console.error('Error calculating distance:', status, response);
            resolve(null);
          }
        });
      } catch (error) {
        console.error('Exception in calculateDistance:', error);
        resolve(null);
      }
    });
  };

  const value = useMemo(() => ({
    isLoaded,
    loadError,
    map,
    autocompleteService,
    placesService,
    geocoder,
    calculateDistance,
  }), [isLoaded, loadError, map, autocompleteService, placesService, geocoder]);

  return (
    <GoogleMapsContext.Provider value={value}>
      {children}
    </GoogleMapsContext.Provider>
  );
};

export const useGoogleMaps = () => {
  const context = useContext(GoogleMapsContext);
  if (context === null) {
    throw new Error('useGoogleMaps must be used within a GoogleMapsProvider');
  }
  return context;
};

// Helper hook to use the map instance
export const useMap = () => {
  const { map } = useGoogleMaps();
  return map;
};

// Helper hook to use the autocomplete service
export const useAutocomplete = () => {
  const { autocompleteService } = useGoogleMaps();
  return autocompleteService;
};

// Helper hook to use the places service
export const usePlaces = () => {
  const { placesService } = useGoogleMaps();
  return placesService;
};

// Helper hook to use the geocoder
export const useGeocoder = () => {
  const { geocoder } = useGoogleMaps();
  return geocoder;
};

export default GoogleMapsContext;
